import { useTranslation } from "react-i18next";
import React from "react";
import styles from "./PastTripReview.module.css";
import { Breadcrumb, Button, Icon, PastTripsCard, SectionHeader, Text } from "@zorni/components-lib";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useReservation } from "../../components/trips/myTrips/hooks/useReservation ";
import TripReviewForm from "../../components/userReview/TripReviewForm/TripReviewForm";
import { useReviewList } from "../../components/userReview/hooks/useReviewList";
import { useZorni } from "../../hooks/useZorni";

const isReviewOwnedByUser = (review, userId) => {
  const authorId = review?.ownerId ?? review?.clientId ?? "";
  return String(authorId) === String(userId ?? "");
};

const hasLegacyAnonymousReview = (reviews) => {
  return (
    Array.isArray(reviews) &&
    reviews.length > 0 &&
    reviews.every((review) => !review?.ownerId && !review?.clientId)
  );
};

const PastTripReview = () => {
  const { t } = useTranslation();
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const { userId } = useZorni();
  const { reservation, loading, error } = useReservation(reservationId);
  const { reviews, loading: reviewsLoading } = useReviewList(
    reservation?.publicationId,
    { currentPage: 0, sizePage: 1000 },
    { enabled: Boolean(reservation?.publicationId) }
  );

  const existingReview = (reviews ?? []).find((review) =>
    isReviewOwnedByUser(review, userId)
  );
  const shouldLockReview = Boolean(existingReview) || hasLegacyAnonymousReview(reviews);

  const handleReviewSubmitted = () => {
    navigate("/my-trips");
  };

  const handleCancel = () => {
    navigate("/my-trips");
  };

  if (loading) {
    return (
      <div className={styles.pageState}>
        <Text as="p" size="md" color="muted">
          {t("review.loading", "Chargement de votre voyage…")}
        </Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.pageState}>
        <Text as="p" size="md" color="danger">
          {t("review.error", "Impossible de charger ce voyage.")}
        </Text>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className={styles.pageState}>
        <Text as="p" size="md" color="muted">
          {t("review.empty", "Aucune donnée trouvée.")}
        </Text>
      </div>
    );
  }

  const trip = reservation?.tripPublicationResume?.trip;
  const mainPicture =
    trip?.pictures?.find((picture) => picture?.isMain)?.link ||
    trip?.pictures?.[0]?.link;

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <Breadcrumb
          className={styles.breadcrumb}
          ariaLabel={t("trips.results.breadcrumb", "Fil d'Ariane")}
          linkComponent={Link}
          items={[
            { label: t("trips.results.home", "Accueil"), to: "/" },
            { label: t("components.myTrips.pageTitle", "Mes voyages"), to: "/my-trips" },
            { label: t("review.title", "Laisser un avis") },
          ]}
        />

        <SectionHeader
          size="page"
          className={styles.header}
          kicker={t("review.badge", "Votre retour d'expérience")}
          title={t("review.title", "Laisser un avis")}
          subtitle={t("review.subtitle", "Partagez ce qui a compté pendant ce voyage. Votre retour aide les prochains voyageurs à choisir avec plus de clarté.")}
        />

        <div className={styles.facts}>
          <span>
            <Icon name="iconCompliance" size="sm" />
            {t("review.criteriaCount", "4 critères de notation")}
          </span>
          <span>
            <Icon name="iconUser" size="sm" />
            {t("review.oneMax", "1 avis maximum par voyageur")}
          </span>
        </div>

        <div className={styles.content}>
          <aside className={styles.summaryColumn}>
            <div className={styles.cardShell}>
              <PastTripsCard
                thumbnail={mainPicture}
                title={trip?.title}
                country={trip?.tripProgramDetails?.[0]?.country}
                startDate={reservation?.tripDate?.startDate}
                endDate={reservation?.tripDate?.endDate}
                showReviewButton={false}
                onReviewClick={() => {}}
              />
            </div>

            <div className={styles.guidanceCard}>
              <Text as="h3" size="xl" weight="semibold">{t("review.guidanceTitle", "Ce que votre avis doit couvrir")}</Text>
              <div className={styles.guidanceList}>
                <div className={styles.guidanceItem}>
                  <Icon name="iconCompliance" size="md" />
                  <Text as="p" size="sm" color="subtle">{t("review.guidance1", "La conformité du voyage avec la promesse initiale.")}</Text>
                </div>
                <div className={styles.guidanceItem}>
                  <Icon name="iconServiceQuality" size="md" />
                  <Text as="p" size="sm" color="subtle">{t("review.guidance2", "La qualité de l'accompagnement, des prestations et du rythme.")}</Text>
                </div>
                <div className={styles.guidanceItem}>
                  <Icon name="iconPriceQuality" size="md" />
                  <Text as="p" size="sm" color="subtle">{t("review.guidance3", "Votre perception du rapport qualité / prix global.")}</Text>
                </div>
              </div>
            </div>
          </aside>

          <div className={styles.formColumn}>
            {reviewsLoading ? (
              <div className={styles.messageCard}>
                <Text as="p" size="md" color="muted">{t("review.loadingReview", "Chargement de votre avis…")}</Text>
              </div>
            ) : shouldLockReview ? (
              <div className={styles.reviewLocked}>
                <div className={styles.lockedIcon}>
                  <Icon name="iconShield" size="xl" />
                </div>
                <div className={styles.lockedText}>
                  <Text as="h2" size="xxl" weight="semibold">{t("review.lockedTitle", "Avis déjà enregistré")}</Text>
                  <Text as="p" size="sm" color="subtle">{t("review.lockedText", "Vous avez déjà partagé votre expérience pour ce voyage. Pour garder des retours fiables, un seul avis est autorisé par utilisateur et par séjour.")}</Text>
                </div>
                <div className={styles.lockedActions}>
                  <Button
                    primary={false}
                    label={t("reservation.back", "Retour à mes voyages")}
                    onClick={() => navigate("/my-trips")}
                  />
                </div>
              </div>
            ) : (
              <TripReviewForm
                tripPublicationId={reservation?.publicationId}
                onSubmit={handleReviewSubmitted}
                onCancel={handleCancel}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PastTripReview;
